import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { collection, onSnapshot, orderBy, query, doc, updateDoc, Timestamp } from 'firebase/firestore';

const db = getFirebaseFirestore();
import {
  Bell,
  BellRing,
  BellOff,
  Package,
  TrendingDown,
  TrendingUp,
  ShoppingCart,
  Target,
  AlertTriangle,
  CheckCircle2,
  Clock,
  X,
  Settings,
  Filter,
  Search,
  MoreVertical,
  Mail,
  Smartphone,
  Globe,
  Volume2,
  VolumeX,
  Trash2,
  Check,
  ChevronRight,
  RefreshCw,
  Zap,
  DollarSign,
  Users,
  BarChart3,
  Eye,
  EyeOff
} from 'lucide-react';

// Types
interface Notification {
  id: string;
  type: 'stock' | 'price' | 'order' | 'roas' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'critical';
  data?: {
    productName?: string;
    sku?: string;
    platform?: string;
    oldValue?: number;
    newValue?: number;
    threshold?: number;
    orderId?: string;
    amount?: number;
  };
}

interface NotificationSettings {
  stockAlerts: {
    enabled: boolean;
    threshold: number;
    channels: ('email' | 'push' | 'sms')[];
  };
  priceAlerts: {
    enabled: boolean;
    percentageChange: number;
    channels: ('email' | 'push' | 'sms')[];
  };
  orderAlerts: {
    enabled: boolean;
    minAmount: number;
    channels: ('email' | 'push' | 'sms')[];
  };
  roasAlerts: {
    enabled: boolean;
    targetRoas: number;
    channels: ('email' | 'push' | 'sms')[];
  };
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
  };
  emailDigest: {
    enabled: boolean;
    frequency: 'daily' | 'weekly';
  };
}

// Mock notifications data
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    type: 'stock',
    title: 'Kritik Stok Uyarısı',
    message: 'iPhone 15 Pro Kılıf - Siyah ürününün stoğu kritik seviyeye düştü.',
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    read: false,
    priority: 'critical',
    data: {
      productName: 'iPhone 15 Pro Kılıf - Siyah',
      sku: 'SKU-1234',
      platform: 'Trendyol',
      newValue: 3,
      threshold: 10
    }
  },
  {
    id: '2',
    type: 'price',
    title: 'Rakip Fiyat Değişikliği',
    message: 'Kablosuz Kulaklık Pro Max ürününde rakip fiyatı %15 düşürdü.',
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    read: false,
    priority: 'high',
    data: {
      productName: 'Kablosuz Kulaklık Pro Max',
      sku: 'SKU-5678',
      platform: 'Hepsiburada',
      oldValue: 1299,
      newValue: 1104
    }
  },
  {
    id: '3',
    type: 'order',
    title: 'Yeni Sipariş',
    message: 'Trendyol üzerinden yeni sipariş alındı.',
    timestamp: new Date(Date.now() - 1000 * 60 * 45),
    read: false,
    priority: 'medium',
    data: {
      orderId: 'TR-2024-12345',
      platform: 'Trendyol',
      amount: 1850
    }
  },
  {
    id: '4',
    type: 'roas',
    title: 'ROAS Hedef Altında',
    message: 'Google Ads kampanyasının ROAS değeri hedefin altına düştü.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: true,
    priority: 'high',
    data: {
      platform: 'Google Ads',
      newValue: 2.8,
      threshold: 3.5
    }
  },
  {
    id: '5',
    type: 'order',
    title: 'Yüksek Tutarlı Sipariş',
    message: 'Amazon TR üzerinden yüksek tutarlı sipariş alındı.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3),
    read: true,
    priority: 'medium',
    data: {
      orderId: 'AMZ-2024-67890',
      platform: 'Amazon TR',
      amount: 4500
    }
  },
  {
    id: '6',
    type: 'stock',
    title: 'Stok Uyarısı',
    message: 'USB-C Hızlı Şarj Kablosu stoğu azalıyor.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
    read: true,
    priority: 'medium',
    data: {
      productName: 'USB-C Hızlı Şarj Kablosu',
      sku: 'SKU-4567',
      platform: 'Trendyol',
      newValue: 12,
      threshold: 20
    }
  },
  {
    id: '7',
    type: 'price',
    title: 'Rakip Fiyat Artışı',
    message: 'Akıllı Saat Ultra ürününde rakip fiyatı %10 artırdı.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8),
    read: true,
    priority: 'low',
    data: {
      productName: 'Akıllı Saat Ultra',
      sku: 'SKU-9012',
      platform: 'n11',
      oldValue: 3500,
      newValue: 3850
    }
  },
  {
    id: '8',
    type: 'system',
    title: 'Bağlantı Güncellendi',
    message: 'Trendyol API bağlantısı başarıyla yenilendi.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    read: true,
    priority: 'low',
    data: {
      platform: 'Trendyol'
    }
  }
];

const DEFAULT_SETTINGS: NotificationSettings = {
  stockAlerts: {
    enabled: true,
    threshold: 10,
    channels: ['email', 'push']
  },
  priceAlerts: {
    enabled: true,
    percentageChange: 5,
    channels: ['email', 'push']
  },
  orderAlerts: {
    enabled: true,
    minAmount: 0,
    channels: ['push']
  },
  roasAlerts: {
    enabled: true,
    targetRoas: 3.5,
    channels: ['email', 'push']
  },
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '08:00'
  },
  emailDigest: {
    enabled: true,
    frequency: 'daily'
  }
};

type FilterType = 'all' | 'stock' | 'price' | 'order' | 'roas' | 'unread';

const NotificationsPage: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  const [activeTab, setActiveTab] = useState<'notifications' | 'settings'>('notifications');
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Firestore'dan gerçek bildirimleri dinle
  useEffect(() => {
    if (!user?.uid) {
      setNotifications(MOCK_NOTIFICATIONS);
      setIsLoading(false);
      return;
    }

    const notifRef = collection(db, 'users', user.uid, 'notifications');
    const q = query(notifRef, orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firestoreNotifs: Notification[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const timestamp = data.timestamp instanceof Timestamp
          ? data.timestamp.toDate()
          : new Date(data.timestamp || Date.now());
        return {
          id: docSnap.id,
          type: data.type || 'system',
          title: data.title || 'Bildirim',
          message: data.message || '',
          timestamp,
          read: data.read || false,
          priority: data.priority || 'medium',
          data: data.data || {},
        } as Notification;
      });

      setNotifications(firestoreNotifs.length > 0 ? firestoreNotifs : MOCK_NOTIFICATIONS);
      setIsLoading(false);
    }, () => {
      setNotifications(MOCK_NOTIFICATIONS);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Az önce';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} dakika önce`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} saat önce`;
    const days = Math.floor(hours / 24);
    return `${days} gün önce`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'stock':
        return <Package className="w-5 h-5" />;
      case 'price':
        return <TrendingDown className="w-5 h-5" />;
      case 'order':
        return <ShoppingCart className="w-5 h-5" />;
      case 'roas':
        return <Target className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getNotificationColor = (type: Notification['type'], priority: Notification['priority']) => {
    if (priority === 'critical') return 'text-red-400 bg-red-500/20';
    if (priority === 'high') return 'text-orange-400 bg-orange-500/20';

    switch (type) {
      case 'stock':
        return 'text-red-400 bg-red-500/20';
      case 'price':
        return 'text-purple-400 bg-purple-500/20';
      case 'order':
        return 'text-green-400 bg-green-500/20';
      case 'roas':
        return 'text-yellow-400 bg-yellow-500/20';
      default:
        return 'text-blue-400 bg-blue-500/20';
    }
  };

  const getPriorityBadge = (priority: Notification['priority']) => {
    switch (priority) {
      case 'critical':
        return <span className="px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">Kritik</span>;
      case 'high':
        return <span className="px-2 py-0.5 text-xs font-medium bg-orange-500/20 text-orange-400 rounded-full">Yüksek</span>;
      case 'medium':
        return <span className="px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full">Orta</span>;
      default:
        return null;
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread' && n.read) return false;
    if (filter !== 'all' && filter !== 'unread' && n.type !== filter) return false;
    if (searchQuery && !n.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !n.message.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const toggleChannel = (
    alertType: keyof Pick<NotificationSettings, 'stockAlerts' | 'priceAlerts' | 'orderAlerts' | 'roasAlerts'>,
    channel: 'email' | 'push' | 'sms'
  ) => {
    setSettings(prev => {
      const currentChannels = prev[alertType].channels;
      const newChannels = currentChannels.includes(channel)
        ? currentChannels.filter(c => c !== channel)
        : [...currentChannels, channel];
      return {
        ...prev,
        [alertType]: {
          ...prev[alertType],
          channels: newChannels
        }
      };
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Bildirimler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BellRing className="w-7 h-7 text-purple-400" />
            Bildirimler
            {unreadCount > 0 && (
              <span className="px-2.5 py-1 text-sm font-medium bg-red-500 text-white rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-gray-400 mt-1">Stok, fiyat, sipariş ve performans bildirimleri</p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-2 bg-[#1a1a2e] rounded-xl p-1">
          <button
            onClick={() => setActiveTab('notifications')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'notifications'
                ? 'bg-purple-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Bell className="w-4 h-4" />
            Bildirimler
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'settings'
                ? 'bg-purple-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Settings className="w-4 h-4" />
            Ayarlar
          </button>
        </div>
      </div>

      {activeTab === 'notifications' ? (
        <>
          {/* Filters & Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Bildirimlerde ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-[#1a1a2e] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
              />
            </div>

            {/* Filter Chips */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {[
                { id: 'all', label: 'Tümü', icon: Bell },
                { id: 'unread', label: 'Okunmamış', icon: Eye },
                { id: 'stock', label: 'Stok', icon: Package },
                { id: 'price', label: 'Fiyat', icon: TrendingDown },
                { id: 'order', label: 'Sipariş', icon: ShoppingCart },
                { id: 'roas', label: 'ROAS', icon: Target },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id as FilterType)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    filter === f.id
                      ? 'bg-purple-500 text-white'
                      : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-white/10'
                  }`}
                >
                  <f.icon className="w-4 h-4" />
                  {f.label}
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-2 px-3 py-2 bg-[#1a1a2e] border border-white/10 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Tümünü Okundu İşaretle
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="space-y-3">
            {filteredNotifications.length === 0 ? (
              <div className="text-center py-16">
                <BellOff className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Bildirim bulunamadı</h3>
                <p className="text-gray-400">
                  {filter !== 'all' ? 'Bu filtreye uygun bildirim yok.' : 'Henüz bildiriminiz bulunmuyor.'}
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`group relative p-4 rounded-2xl border transition-all cursor-pointer hover:border-purple-500/30 ${
                    notification.read
                      ? 'bg-[#1a1a2e]/50 border-white/5'
                      : 'bg-[#1a1a2e] border-purple-500/20'
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification.type, notification.priority)}`}>
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-semibold ${notification.read ? 'text-gray-300' : 'text-white'}`}>
                              {notification.title}
                            </h3>
                            {!notification.read && (
                              <span className="w-2 h-2 rounded-full bg-purple-500" />
                            )}
                            {getPriorityBadge(notification.priority)}
                          </div>
                          <p className="text-sm text-gray-400 mb-2">{notification.message}</p>

                          {/* Additional Data */}
                          {notification.data && (
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                              {notification.data.platform && (
                                <span className="flex items-center gap-1">
                                  <Globe className="w-3 h-3" />
                                  {notification.data.platform}
                                </span>
                              )}
                              {notification.data.sku && (
                                <span>{notification.data.sku}</span>
                              )}
                              {notification.data.orderId && (
                                <span className="font-mono">{notification.data.orderId}</span>
                              )}
                              {notification.data.amount && (
                                <span className="text-green-400 font-medium">
                                  {formatCurrency(notification.data.amount)}
                                </span>
                              )}
                              {notification.data.oldValue !== undefined && notification.data.newValue !== undefined && notification.type === 'price' && (
                                <span className="flex items-center gap-1">
                                  <span className="line-through text-gray-600">{formatCurrency(notification.data.oldValue)}</span>
                                  <ChevronRight className="w-3 h-3" />
                                  <span className={notification.data.newValue < notification.data.oldValue ? 'text-red-400' : 'text-green-400'}>
                                    {formatCurrency(notification.data.newValue)}
                                  </span>
                                </span>
                              )}
                              {notification.type === 'stock' && notification.data.newValue !== undefined && (
                                <span className="text-red-400">
                                  Stok: {notification.data.newValue} adet (Min: {notification.data.threshold})
                                </span>
                              )}
                              {notification.type === 'roas' && notification.data.newValue !== undefined && (
                                <span className="text-yellow-400">
                                  ROAS: {notification.data.newValue}x (Hedef: {notification.data.threshold}x)
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Time & Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatTimeAgo(notification.timestamp)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        /* Settings Tab */
        <div className="space-y-6">
          {/* Alert Types */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Stock Alerts */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Stok Uyarıları</h3>
                    <p className="text-sm text-gray-400">Stok kritik seviyeye düştüğünde</p>
                  </div>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    stockAlerts: { ...prev.stockAlerts, enabled: !prev.stockAlerts.enabled }
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors ${settings.stockAlerts.enabled ? 'bg-purple-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.stockAlerts.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.stockAlerts.enabled && (
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Kritik Stok Eşiği</label>
                    <input
                      type="number"
                      value={settings.stockAlerts.threshold}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        stockAlerts: { ...prev.stockAlerts, threshold: parseInt(e.target.value) || 0 }
                      }))}
                      className="w-full px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Bildirim Kanalları</label>
                    <div className="flex gap-2">
                      {[
                        { id: 'email', icon: Mail, label: 'E-posta' },
                        { id: 'push', icon: Bell, label: 'Push' },
                        { id: 'sms', icon: Smartphone, label: 'SMS' },
                      ].map((channel) => (
                        <button
                          key={channel.id}
                          onClick={() => toggleChannel('stockAlerts', channel.id as 'email' | 'push' | 'sms')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                            settings.stockAlerts.channels.includes(channel.id as 'email' | 'push' | 'sms')
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-[#12121a] text-gray-400 border border-white/10'
                          }`}
                        >
                          <channel.icon className="w-4 h-4" />
                          {channel.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Price Alerts */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Fiyat Uyarıları</h3>
                    <p className="text-sm text-gray-400">Rakip fiyat değiştirdiğinde</p>
                  </div>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    priceAlerts: { ...prev.priceAlerts, enabled: !prev.priceAlerts.enabled }
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors ${settings.priceAlerts.enabled ? 'bg-purple-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.priceAlerts.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.priceAlerts.enabled && (
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Minimum Fiyat Değişimi (%)</label>
                    <input
                      type="number"
                      value={settings.priceAlerts.percentageChange}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        priceAlerts: { ...prev.priceAlerts, percentageChange: parseInt(e.target.value) || 0 }
                      }))}
                      className="w-full px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Bildirim Kanalları</label>
                    <div className="flex gap-2">
                      {[
                        { id: 'email', icon: Mail, label: 'E-posta' },
                        { id: 'push', icon: Bell, label: 'Push' },
                        { id: 'sms', icon: Smartphone, label: 'SMS' },
                      ].map((channel) => (
                        <button
                          key={channel.id}
                          onClick={() => toggleChannel('priceAlerts', channel.id as 'email' | 'push' | 'sms')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                            settings.priceAlerts.channels.includes(channel.id as 'email' | 'push' | 'sms')
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-[#12121a] text-gray-400 border border-white/10'
                          }`}
                        >
                          <channel.icon className="w-4 h-4" />
                          {channel.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Order Alerts */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <ShoppingCart className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Sipariş Bildirimleri</h3>
                    <p className="text-sm text-gray-400">Yeni sipariş geldiğinde</p>
                  </div>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    orderAlerts: { ...prev.orderAlerts, enabled: !prev.orderAlerts.enabled }
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors ${settings.orderAlerts.enabled ? 'bg-purple-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.orderAlerts.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.orderAlerts.enabled && (
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Minimum Sipariş Tutarı (TL)</label>
                    <input
                      type="number"
                      value={settings.orderAlerts.minAmount}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        orderAlerts: { ...prev.orderAlerts, minAmount: parseInt(e.target.value) || 0 }
                      }))}
                      className="w-full px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                    <p className="text-xs text-gray-500 mt-1">0 = Tüm siparişler için bildirim</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Bildirim Kanalları</label>
                    <div className="flex gap-2">
                      {[
                        { id: 'email', icon: Mail, label: 'E-posta' },
                        { id: 'push', icon: Bell, label: 'Push' },
                        { id: 'sms', icon: Smartphone, label: 'SMS' },
                      ].map((channel) => (
                        <button
                          key={channel.id}
                          onClick={() => toggleChannel('orderAlerts', channel.id as 'email' | 'push' | 'sms')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                            settings.orderAlerts.channels.includes(channel.id as 'email' | 'push' | 'sms')
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-[#12121a] text-gray-400 border border-white/10'
                          }`}
                        >
                          <channel.icon className="w-4 h-4" />
                          {channel.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ROAS Alerts */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">ROAS Uyarıları</h3>
                    <p className="text-sm text-gray-400">Hedefin altına düştüğünde</p>
                  </div>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    roasAlerts: { ...prev.roasAlerts, enabled: !prev.roasAlerts.enabled }
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors ${settings.roasAlerts.enabled ? 'bg-purple-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.roasAlerts.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.roasAlerts.enabled && (
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Hedef ROAS</label>
                    <input
                      type="number"
                      step="0.1"
                      value={settings.roasAlerts.targetRoas}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        roasAlerts: { ...prev.roasAlerts, targetRoas: parseFloat(e.target.value) || 0 }
                      }))}
                      className="w-full px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Bildirim Kanalları</label>
                    <div className="flex gap-2">
                      {[
                        { id: 'email', icon: Mail, label: 'E-posta' },
                        { id: 'push', icon: Bell, label: 'Push' },
                        { id: 'sms', icon: Smartphone, label: 'SMS' },
                      ].map((channel) => (
                        <button
                          key={channel.id}
                          onClick={() => toggleChannel('roasAlerts', channel.id as 'email' | 'push' | 'sms')}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                            settings.roasAlerts.channels.includes(channel.id as 'email' | 'push' | 'sms')
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-[#12121a] text-gray-400 border border-white/10'
                          }`}
                        >
                          <channel.icon className="w-4 h-4" />
                          {channel.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Additional Settings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Quiet Hours */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <VolumeX className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Sessiz Saatler</h3>
                    <p className="text-sm text-gray-400">Belirli saatlerde bildirim alma</p>
                  </div>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    quietHours: { ...prev.quietHours, enabled: !prev.quietHours.enabled }
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors ${settings.quietHours.enabled ? 'bg-purple-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.quietHours.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.quietHours.enabled && (
                <div className="flex gap-4 pt-4 border-t border-white/5">
                  <div className="flex-1">
                    <label className="text-sm text-gray-400 mb-2 block">Başlangıç</label>
                    <input
                      type="time"
                      value={settings.quietHours.start}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        quietHours: { ...prev.quietHours, start: e.target.value }
                      }))}
                      className="w-full px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm text-gray-400 mb-2 block">Bitiş</label>
                    <input
                      type="time"
                      value={settings.quietHours.end}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        quietHours: { ...prev.quietHours, end: e.target.value }
                      }))}
                      className="w-full px-4 py-2 bg-[#12121a] border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Email Digest */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">E-posta Özeti</h3>
                    <p className="text-sm text-gray-400">Bildirimlerin özet e-postası</p>
                  </div>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    emailDigest: { ...prev.emailDigest, enabled: !prev.emailDigest.enabled }
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors ${settings.emailDigest.enabled ? 'bg-purple-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.emailDigest.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.emailDigest.enabled && (
                <div className="pt-4 border-t border-white/5">
                  <label className="text-sm text-gray-400 mb-2 block">Gönderim Sıklığı</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'daily', label: 'Günlük' },
                      { id: 'weekly', label: 'Haftalık' },
                    ].map((freq) => (
                      <button
                        key={freq.id}
                        onClick={() => setSettings(prev => ({
                          ...prev,
                          emailDigest: { ...prev.emailDigest, frequency: freq.id as 'daily' | 'weekly' }
                        }))}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          settings.emailDigest.frequency === freq.id
                            ? 'bg-purple-500 text-white'
                            : 'bg-[#12121a] text-gray-400 border border-white/10'
                        }`}
                      >
                        {freq.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity">
              <Check className="w-5 h-5" />
              Ayarları Kaydet
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
